import { useEffect, useRef, useState } from 'react'
import { aStar, djikstra } from './algorithms';
import NodeC from './node';
import AlgorithmInfo from '../components/AlgorithmInfo';
import './node.css';

export interface NodeI {
    distance: number,
    col: number,
    row: number,
    isSrc: boolean,
    isDest: boolean,
    isVisited: boolean,
    isPath:boolean,
    isWeight:boolean,
    isWall:boolean,
    weight: number,
    previous: NodeI | null,
}
export default function PathFinding() {
    const rows = 15, cols = 25;
    enum Algorithms {
        djikstra = 'Dijkstra',
        aStar = 'A*',
    }
    const [algo, setalgo]: any = useState(Algorithms.djikstra);
    const [grid, setgrid] = useState<NodeI[][]>([...Array(rows)].map(x => Array(cols)));
    const [change, setchange] = useState(0);
    const [obstacle, setobstacle] = useState(0);
    const [isMouseDown, setisMouseDown] = useState(false);
    const [isRunning, setIsRunning] = useState(false);
    const [speed, setSpeed] = useState(50);
    const [stats, setStats] = useState({
        nodesVisited: 0,
        pathLength: 0,
        timeElapsed: 0
    });
    const [draggedNode, setDraggedNode] = useState<{type: 'start' | 'end', row: number, col: number} | null>(null);

    useEffect(() => {
        resetGrid();
    }, []);
    async function search(){
        if (isRunning) return;
        
        setIsRunning(true);
        setStats(prev => ({ ...prev, nodesVisited: 0, pathLength: 0, timeElapsed: 0 }));
        
        const startTime = Date.now();
        
        switch (algo) {
            case Algorithms.djikstra:
                await djikstra(grid, setchange, speed, (stats: { nodesVisited?: number; pathLength?: number }) => {
                    setStats(prev => ({ ...prev, ...stats }));
                });
                break;
            case Algorithms.aStar:
                await aStar(grid, setchange, speed, (stats: { nodesVisited?: number; pathLength?: number }) => {
                    setStats(prev => ({ ...prev, ...stats }));
                });
                break;
            default:
                break;
        }
        
        const endTime = Date.now();
        setStats(prev => ({ ...prev, timeElapsed: endTime - startTime }));
        setIsRunning(false);
    }
    function resetGrid() {
        const newGrid = [...Array(rows)].map(x => Array(cols));
        for (let i = 0; i < rows; i++) {
            for (let j = 0; j < cols; j++) {
                newGrid[i][j] = { 
                    row: i, 
                    col: j, 
                    distance: Number.MAX_VALUE, 
                    isSrc: false, 
                    isDest: false, 
                    isVisited: false,
                    isPath: false, 
                    previous: null, 
                    weight: 1,
                    isWeight: false,
                    isWall: false
                };
            }
        }
        
        // Set start and end points
        newGrid[Math.floor(rows/2)][Math.floor(cols/4)].isSrc = true;
        newGrid[Math.floor(rows/2)][Math.floor(cols/4)].distance = 0;
        newGrid[Math.floor(rows/2)][Math.floor(3*cols/4)].isDest = true;
        
        setgrid(newGrid);
        setchange(prev => prev + 1);
        setStats({ nodesVisited: 0, pathLength: 0, timeElapsed: 0 });
    }
    function handleAlgoChange(e: any) {
        setalgo(e.currentTarget.value);
    }
    
    const handleStartDrag = (row: number, col: number) => {
        if (isRunning) return;
        setDraggedNode({type: 'start', row, col});
    };
    
    const handleEndDrag = (row: number, col: number) => {
        if (isRunning) return;
        setDraggedNode({type: 'end', row, col});
    };
    
    const handleNodeDrop = (targetRow: number, targetCol: number) => {
        if (!draggedNode || isRunning) return;
        
        const newGrid = grid.map(row => 
            row.map(node => ({ ...node }))
        );
        
        // Remove old start/end
        if (draggedNode.type === 'start') {
            newGrid[draggedNode.row][draggedNode.col].isSrc = false;
        } else {
            newGrid[draggedNode.row][draggedNode.col].isDest = false;
        }
        
        // Set new start/end
        if (draggedNode.type === 'start') {
            newGrid[targetRow][targetCol].isSrc = true;
            newGrid[targetRow][targetCol].isDest = false;
        } else {
            newGrid[targetRow][targetCol].isDest = true;
            newGrid[targetRow][targetCol].isSrc = false;
        }
        
        setgrid(newGrid);
        setDraggedNode(null);
        setchange(prev => prev + 1);
    };
    const ref = useRef<HTMLDivElement>(null);
    
    return (
        <div>
            <div className="control-panel">
                <div className="control-group">
                    <label className="control-label">Algorithm:</label>
                    <select className="control-select" onChange={handleAlgoChange} value={algo}>
                        <option value={Algorithms.djikstra}>{Algorithms.djikstra}</option>
                        <option value={Algorithms.aStar}>{Algorithms.aStar}</option>
                    </select>
                </div>
                
                <div className="control-group">
                    <label className="control-label">Speed:</label>
                    <div className="speed-control">
                        <input 
                            type="range" 
                            min="10" 
                            max="200" 
                            value={speed} 
                            onChange={(e) => setSpeed(Number(e.target.value))}
                            className="speed-slider"
                        />
                        <span>{speed}ms</span>
                    </div>
                </div>
                
                <div className="control-group">
                    <button 
                        className={`control-button ${obstacle === 1 ? 'primary' : 'secondary'}`}
                        onClick={() => setobstacle(obstacle === 1 ? 0 : 1)}
                        disabled={isRunning}
                    >
                        {obstacle === 1 ? '✓ Weight' : 'Add Weight'}
                    </button>
                    <button 
                        className={`control-button ${obstacle === 2 ? 'primary' : 'secondary'}`}
                        onClick={() => setobstacle(obstacle === 2 ? 0 : 2)}
                        disabled={isRunning}
                    >
                        {obstacle === 2 ? '✓ Wall' : 'Add Wall'}
                    </button>
                    <button 
                        className={`control-button ${obstacle === 0 ? 'primary' : 'secondary'}`}
                        onClick={() => {
                            setobstacle(0);
                            // Clear all walls and weights from the grid
                            const clearedGrid = grid.map(row => 
                                row.map(node => ({
                                    ...node,
                                    isWall: false,
                                    isWeight: false,
                                    weight: 1
                                }))
                            );
                            setgrid(clearedGrid);
                            setchange(prev => prev + 1);
                        }}
                        disabled={isRunning}
                    >
                        Clear
                    </button>
                </div>
                
                <div className="control-group">
                    <button className="control-button secondary" onClick={resetGrid} disabled={isRunning}>
                        Reset Grid
                    </button>
                    <button 
                        className="control-button primary" 
                        onClick={search}
                        disabled={isRunning}
                    >
                        {isRunning ? 'Running...' : 'Start Search'}
                    </button>
                </div>
                
                <div className="control-group stats-inline">
                    <div className="stat-item-inline">
                        <span className="stat-value-inline">{stats.nodesVisited}</span>
                        <span className="stat-label-inline">Visited</span>
                    </div>
                    <div className="stat-item-inline">
                        <span className="stat-value-inline">{stats.pathLength}</span>
                        <span className="stat-label-inline">Distance</span>
                    </div>
                    <div className="stat-item-inline">
                        <span className="stat-value-inline">{stats.timeElapsed}ms</span>
                        <span className="stat-label-inline">Time</span>
                    </div>
                </div>
            </div>
            
            <div className="pathfinding-grid-container">
                {/* <div className="grid-instructions">
                    <p>🎯 Click and drag to add walls or weights • 🚀 Start point • 🎯 End point</p>
                </div> */}
                <div 
                    className="pathfinding-grid"
                    onMouseDown={() => setisMouseDown(true)}
                    onMouseUp={() => setisMouseDown(false)}
                >
                    {grid.map((row, rowIdx) => (
                        <div key={rowIdx} className="grid-row">
                            {row.map((node, colIdx) => (
                                <NodeC 
                                    key={`${rowIdx}-${colIdx}`}
                                    nodeC={node} 
                                    obstacle={obstacle} 
                                    isMouseDown={isMouseDown}
                                    onNodeChange={(updatedNode) => {
                                        const newGrid = grid.map((row, rIdx) => 
                                            rIdx === rowIdx 
                                                ? row.map((node, cIdx) => cIdx === colIdx ? updatedNode : node)
                                                : row
                                        );
                                        setgrid(newGrid);
                                        setchange(prev => prev + 1);
                                    }}
                                    onStartDrag={handleStartDrag}
                                    onEndDrag={handleEndDrag}
                                    onNodeDrop={handleNodeDrop}
                                />
                            ))}
                        </div>
                    ))}
                </div>
            </div>
            
            <AlgorithmInfo algorithm={algo} type="pathfinding" />
        </div>
    )
}
